import React, { useState, useMemo } from "react";
import { View, Text, StyleSheet, Image, Pressable, ScrollView } from "react-native";
import { useNavigation } from "@react-navigation/native";
import NtTextInput from "../../components/NtTextInput";
import NtMarketingPageContainer from "../components/general/NtMarketingPageContainer";
import BaseMarketingController from "./BaseMarketingController";
import { useTheme } from "../../context/ThemeContext";
import { darkTheme } from "../../config/themes";
import { useMobile } from "../../hooks/useMobile";
import routes from "../../navigation/routes";
import { ForceDarkTheme } from "../components/voxcraft/_shared";

const TENANT_SIZES = [
  "Just exploring",
  "1–50 extensions",
  "50–500 extensions",
  "500+ extensions",
  "Multi-tenant (agency / MSP)",
];

function LearnMoreView() {
  const { theme } = useTheme();
  const nav = useNavigation();
  const isMobile = useMobile();
  const styles = useMemo(() => makeStyles(theme, isMobile), [theme, isMobile]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [size, setSize] = useState("");
  const [current, setCurrent] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const canSubmit = name.trim() && email.includes("@") && company.trim() && size;

  return (
    <View style={styles.page}>
      <Text style={styles.breadcrumb}>HOME · LEARN MORE</Text>

      <View style={styles.shell}>
        {/* LEFT — pitch */}
        <View style={styles.pitch}>
          <View style={styles.eyebrow}>
            <Text style={styles.eyebrowText}>✉ TELL US WHAT YOU'RE BUILDING</Text>
          </View>
          <Text style={styles.h1}>Let's start with</Text>
          <Text style={styles.h1Soft}>what you're working on.</Text>
          <Text style={styles.lede}>
            Tell us a bit about your stack and what you're trying to solve.
            We'll come back with a walkthrough tailored to it, not a generic sales pitch.
          </Text>

          <View style={styles.metaRow}>
            <View style={styles.metaItem}>
              <View style={styles.metaIcon}><Text style={styles.metaIconText}>⏱</Text></View>
              <View>
                <Text style={styles.metaLbl}>REPLY TIME</Text>
                <Text style={styles.metaVal}>≤ 1 business day</Text>
              </View>
            </View>
            <View style={styles.metaItem}>
              <View style={styles.metaIcon}><Text style={styles.metaIconText}>🛈</Text></View>
              <View>
                <Text style={styles.metaLbl}>OUTCOME</Text>
                <Text style={styles.metaVal}>Tailored walkthrough</Text>
              </View>
            </View>
          </View>

          <View style={styles.hostCard}>
            <View style={styles.avatar}>
              <Image source={{ uri: "https://randomuser.me/api/portraits/men/32.jpg" }} style={styles.avatarImg} />
              <View style={styles.avatarStatus} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.hostName}>Diego Mora</Text>
              <Text style={styles.hostRole}>Solutions engineer · Voxcraft</Text>
              <Text style={styles.hostPing}>● USUALLY REPLIES IN UNDER 2 HOURS</Text>
            </View>
          </View>

          <View style={styles.trustPills}>
            {["HOLLAND AMERICA", "CUNARD", "CARNIVAL UK", "U.S. NAVY", "INMARSAT"].map((p, i) => (
              <Text key={i} style={styles.trustPill}>{p}</Text>
            ))}
          </View>
        </View>

        {/* RIGHT — form */}
        <View style={styles.formSide}>
          {confirmed ? (
            <Confirmation theme={theme} name={name} email={email} onBack={() => nav.navigate(routes.marketing)} />
          ) : (
            <>
              <Text style={styles.formH2}>Tell us about you</Text>
              <Text style={styles.formSub}>
                A few quick fields. We use this to make sure the right person on our side picks it up, and to
                skip the generic intro on the call.
              </Text>

              <View style={styles.fieldRow}>
                <View style={styles.field}>
                  <Text style={styles.fieldLbl}>FULL NAME</Text>
                  <NtTextInput value={name} onChangeText={setName} placeholder="Jordan Barnes" />
                </View>
                <View style={styles.field}>
                  <Text style={styles.fieldLbl}>WORK EMAIL</Text>
                  <NtTextInput value={email} onChangeText={setEmail} placeholder="you@company.com" />
                </View>
              </View>

              <View style={styles.fieldRow}>
                <View style={styles.field}>
                  <Text style={styles.fieldLbl}>COMPANY</Text>
                  <NtTextInput value={company} onChangeText={setCompany} placeholder="Acme Line" />
                </View>
                <View style={styles.field}>
                  <Text style={styles.fieldLbl}>ROLE</Text>
                  <NtTextInput value={role} onChangeText={setRole} placeholder="IT Director" />
                </View>
              </View>

              <View style={styles.fieldRow}>
                <View style={styles.field}>
                  <Text style={styles.fieldLbl}>TENANT SIZE</Text>
                  <View style={styles.selectWrap}>
                    {/* React-native-web supports raw <select> via the Picker pattern; use a simple chip-row instead */}
                    <View style={styles.sizeRow}>
                      {TENANT_SIZES.map((s) => {
                        const selected = size === s;
                        return (
                          <Pressable key={s} onPress={() => setSize(s)} style={[styles.sizeChip, { borderColor: selected ? theme.brand2 : theme.borderSoft, backgroundColor: selected ? theme.pillBg : theme.bg2 }]}>
                            <Text style={[styles.sizeChipText, { color: selected ? theme.brand2 : theme.textDim }]}>{s}</Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>
                </View>
                <View style={styles.field}>
                  <Text style={styles.fieldLbl}>CURRENT SETUP <Text style={styles.opt}>(optional)</Text></Text>
                  <NtTextInput value={current} onChangeText={setCurrent} placeholder="e.g. RingCentral, on-prem Asterisk, no PBX yet" />
                </View>
              </View>

              <View style={styles.field}>
                <Text style={styles.fieldLbl}>WHAT ARE YOU TRYING TO SOLVE?</Text>
                <NtTextInput
                  value={notes} onChangeText={setNotes}
                  placeholder="We need a PBX for 8 tenants across 3 sites with IVR, call recording, and per-tenant DIDs…"
                  multiline numberOfLines={5}
                />
              </View>

              <Pressable
                onPress={() => canSubmit && setConfirmed(true)}
                style={({ pressed }) => [
                  styles.submitBtn,
                  { backgroundColor: canSubmit ? theme.brand2 : theme.surface2, opacity: canSubmit ? (pressed ? 0.85 : 1) : 0.5 },
                ]}>
                <Text style={[styles.submitBtnText, { color: canSubmit ? "#fff" : theme.textMute }]}>Send it over →</Text>
              </Pressable>

              <Text style={styles.privacy}>
                By submitting, you agree to our privacy policy. We won't add you to a drip campaign, just an
                actual human reply.
              </Text>
            </>
          )}
        </View>
      </View>
    </View>
  );
}

function Confirmation({ theme, name, email, onBack }) {
  const firstName = (name.trim().split(" ")[0]) || "you";
  return (
    <View style={{ alignItems: "center", paddingVertical: 40 }}>
      <View style={{
        width: 64, height: 64, borderRadius: 32,
        backgroundColor: "rgba(76,183,130,0.12)", borderColor: "rgba(76,183,130,0.4)", borderWidth: 1,
        alignItems: "center", justifyContent: "center", marginBottom: 22,
      }}>
        <Text style={{ color: theme.green, fontSize: 30 }}>✓</Text>
      </View>
      <Text style={{ color: theme.text, fontSize: 26, fontWeight: "700", marginBottom: 10 }}>Got it. Thanks!</Text>
      <Text style={{ color: theme.textDim, fontSize: 14.5, lineHeight: 22, textAlign: "center", maxWidth: 440, marginBottom: 28 }}>
        Your note is on its way to <Text style={{ color: theme.text, fontWeight: "600" }}>{firstName}</Text>'s
        assigned solutions engineer. You'll hear back at{" "}
        <Text style={{ color: theme.text, fontWeight: "600" }}>{email}</Text> within one business day.
      </Text>
      <Pressable onPress={onBack} style={{ paddingHorizontal: 22, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: theme.borderSoft, backgroundColor: theme.surface }}>
        <Text style={{ color: theme.textDim, fontSize: 13 }}>← Back to site</Text>
      </Pressable>
    </View>
  );
}

class LearnMoreController extends BaseMarketingController {
  state = {};
  componentDidMount() {
    super.componentDidMount && super.componentDidMount();
    if (typeof document !== "undefined") {
      document.title = "Learn more | Voxcraft";
    }
  }
  render() {
    return (
      <ForceDarkTheme>
        <View style={{ width: "100%", minHeight: "100%", backgroundColor: darkTheme.bg }}>
          <NtMarketingPageContainer>
            <LearnMoreView />
          </NtMarketingPageContainer>
        </View>
      </ForceDarkTheme>
    );
  }
}

const makeStyles = (theme, isMobile) => StyleSheet.create({
  page: { width: "100%", maxWidth: 1100, marginHorizontal: "auto", paddingHorizontal: 28, paddingVertical: 56 },
  breadcrumb: { fontSize: 11, fontFamily: "Menlo", color: theme.textMute, letterSpacing: 0.6, marginBottom: 18 },

  shell: {
    flexDirection: isMobile ? "column" : "row",
    borderWidth: 1, borderColor: theme.borderSoft, borderRadius: 16, overflow: "hidden", backgroundColor: theme.bg2,
  },

  pitch: { flex: 1, padding: 36, borderRightWidth: isMobile ? 0 : 1, borderBottomWidth: isMobile ? 1 : 0, borderColor: theme.borderSoft },
  eyebrow: { alignSelf: "flex-start", borderWidth: 1, borderColor: theme.pillBorder, backgroundColor: theme.pillBg, borderRadius: 999, paddingHorizontal: 11, paddingVertical: 5, marginBottom: 22 },
  eyebrowText: { color: theme.brand2, fontSize: 11, fontWeight: "700", letterSpacing: 1.6 },
  h1: { color: theme.text, fontSize: 30, fontWeight: "700", letterSpacing: -0.6, lineHeight: 34 },
  h1Soft: { color: theme.textDim, fontSize: 30, fontWeight: "500", letterSpacing: -0.6, lineHeight: 34, marginBottom: 16 },
  lede: { color: theme.textDim, fontSize: 14.5, lineHeight: 22, marginBottom: 22 },

  metaRow: { flexDirection: "row", gap: 24, paddingVertical: 14, borderTopWidth: 1, borderBottomWidth: 1, borderColor: theme.borderSoft, marginBottom: 22 },
  metaItem: { flexDirection: "row", alignItems: "center", gap: 10 },
  metaIcon: { width: 30, height: 30, borderRadius: 7, backgroundColor: theme.pillBg, borderColor: theme.pillBorder, borderWidth: 1, alignItems: "center", justifyContent: "center", marginRight: 10 },
  metaIconText: { color: theme.brand2, fontSize: 14, fontWeight: "700" },
  metaLbl: { fontSize: 10, fontFamily: "Menlo", color: theme.textMute, letterSpacing: 0.8 },
  metaVal: { fontSize: 13, color: theme.text, fontWeight: "500" },

  hostCard: { flexDirection: "row", alignItems: "center", padding: 14, borderRadius: 10, borderWidth: 1, borderColor: theme.borderSoft, backgroundColor: theme.surface, marginBottom: 22 },
  avatar: { width: 48, height: 48, borderRadius: 24, overflow: "hidden", marginRight: 14, backgroundColor: theme.surface3, position: "relative" },
  avatarImg: { width: 48, height: 48 },
  avatarStatus: { position: "absolute", bottom: 1, right: 1, width: 12, height: 12, borderRadius: 6, backgroundColor: theme.green, borderWidth: 2, borderColor: theme.surface },
  hostName: { color: theme.text, fontSize: 14, fontWeight: "600" },
  hostRole: { color: theme.textDim, fontSize: 12 },
  hostPing: { marginTop: 4, fontSize: 10, fontFamily: "Menlo", letterSpacing: 0.6, color: theme.green },

  trustPills: { flexDirection: "row", gap: 6, flexWrap: "wrap", borderTopWidth: 1, borderColor: theme.borderSoft, paddingTop: 14, marginTop: 22 },
  trustPill: { fontSize: 10, color: theme.textMute, fontFamily: "Menlo", letterSpacing: 0.6, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 999, borderWidth: 1, borderColor: theme.borderSoft, backgroundColor: theme.surface, marginRight: 6, marginBottom: 6 },

  formSide: { flex: 1, padding: 36, gap: 14 },
  formH2: { color: theme.text, fontSize: 22, fontWeight: "600", marginBottom: 6 },
  formSub: { color: theme.textDim, fontSize: 13.5, lineHeight: 20, marginBottom: 14 },
  fieldRow: { flexDirection: isMobile ? "column" : "row", gap: 12 },
  field: { flex: 1, gap: 6, marginBottom: 4 },
  fieldLbl: { fontSize: 11, fontWeight: "600", color: theme.textMute, letterSpacing: 0.6, fontFamily: "Menlo" },
  opt: { color: theme.textMute, fontWeight: "500", letterSpacing: 0.3 },
  selectWrap: { },
  sizeRow: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  sizeChip: { paddingHorizontal: 11, paddingVertical: 8, borderRadius: 8, borderWidth: 1, marginRight: 6, marginBottom: 6 },
  sizeChipText: { fontSize: 12, fontWeight: "500" },

  submitBtn: { paddingHorizontal: 22, paddingVertical: 14, borderRadius: 10, alignItems: "center", justifyContent: "center", alignSelf: "flex-start", marginTop: 8 },
  submitBtnText: { fontSize: 14, fontWeight: "600" },

  privacy: { fontSize: 11, color: theme.textMute, lineHeight: 18, marginTop: 4 },
});

export default LearnMoreController;
